import { fetchJson } from "../net/fetch";

/**
 * Simulation run parameters.
 *
 * @typedef {Object} SimulationParameters
 * @property {number} simulationDuration - Total simulation duration in minutes.
 * @property {number} warmUpPeriod - Warm-up period in minutes.
 * @property {number} replicationCount - Number of replications to run.
 */

/**
 * Payload for creating a simulation.
 *
 * @typedef {Object} CreateSimulationPayload
 * @property {string} name - Display name for the simulation scenario.
 * @property {SimulationParameters} parameters - Simulation configuration.
 */

/**
 * A simulation scenario attached to a facility twin.
 *
 * @typedef {Object} Simulation
 * @property {string} id - Simulation identifier.
 * @property {string} name - Display name for the simulation scenario.
 * @property {SimulationParameters} parameters - Simulation configuration.
 * @property {Array<{fileName: string}>} [artifacts] - Output artifacts when the simulation completes.
 */

/**
 * Pre-signed download URL for a simulation artifact.
 *
 * @typedef {Object} SimulationArtifactDownloadUrl
 * @property {string} url - Time-limited URL to download the artifact.
 */

/**
 * Facility-scoped simulation API client.
 *
 * Accessed via {@link Autodesk.Tandem.DtFacility#simulations}.
 *
 * @alias Autodesk.Tandem.DtSimulations
 */
export class DtSimulations {

	/**
	 * @constructor
	 *
	 * @param {import("./DtFacility").DtFacility} facility - The facility these simulations belong to.
	 */
	constructor(facility) {
		this.facility = facility;
	}

	/**
	 * Lists simulations for the facility.
	 *
	 * @returns {Promise<Array.<Simulation>>}
	 *
	 * @alias Autodesk.Tandem.DtSimulations#fetchSimulations
	 */
	fetchSimulations() {
		return fetchJson(this.facility.loadContext, `/twins/${this.facility.urn()}/simulations`);
	}

	/**
	 * Fetches simulation status and data by id.
	 *
	 * @param {string} simId
	 * @returns {Promise<Simulation>}
	 *
	 * @alias Autodesk.Tandem.DtSimulations#fetchSimulation
	 */
	fetchSimulation(simId) {
		return fetchJson(this.facility.loadContext, `/twins/${this.facility.urn()}/simulations/${simId}`);
	}

	/**
	 * Creates a simulation for the facility.
	 *
	 * @param {CreateSimulationPayload} payload
	 * @returns {Promise<Simulation>}
	 *
	 * @example
	 * await facility.simulations.createSimulation({
	 *   name: "Scenario 1",
	 *   parameters: {
	 *     simulationDuration: 480,
	 *     warmUpPeriod: 60,
	 *     replicationCount: 1
	 *   }
	 * });
	 *
	 * @alias Autodesk.Tandem.DtSimulations#createSimulation
	 */
	createSimulation(payload) {
		return fetchJson(
			this.facility.loadContext,
			`/twins/${this.facility.urn()}/simulations`,
			"POST",
			payload
		);
	}

	/**
	 * Cancels a running simulation.
	 *
	 * @param {string} simId
	 * @returns {Promise<Simulation>}
	 *
	 * @alias Autodesk.Tandem.DtSimulations#cancelSimulation
	 */
	cancelSimulation(simId) {
		return fetchJson(
			this.facility.loadContext,
			`/twins/${this.facility.urn()}/simulations/${simId}/cancel`,
			"POST"
		);
	}

	/**
	 * Fetches a pre-signed download URL for a simulation artifact.
	 *
	 * @param {string} simId
	 * @param {string} resultsFileName
	 * @returns {Promise<SimulationArtifactDownloadUrl>}
	 *
	 * @alias Autodesk.Tandem.DtSimulations#fetchSimulationArtifactDownloadUrl
	 */
	fetchSimulationArtifactDownloadUrl(simId, resultsFileName) {
		return fetchJson(
			this.facility.loadContext,
			`/twins/${this.facility.urn()}/simulations/${simId}/artifacts/${encodeURIComponent(resultsFileName)}/download-url`
		);
	}

	/**
	 * Deletes a simulation.
	 *
	 * @param {string} simId
	 * @returns {Promise<void>}
	 *
	 * @alias Autodesk.Tandem.DtSimulations#deleteSimulation
	 */
	deleteSimulation(simId) {
		return fetchJson(
			this.facility.loadContext,
			`/twins/${this.facility.urn()}/simulations/${simId}`,
			"DELETE"
		);
	}
}
